import { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useMoodTheme, MOOD_THEMES } from "../contexts/MoodThemeContext";

// Floating particle component
const FloatingParticle = ({ emoji, delay, duration, size }) => {
  const startX = Math.random() * 100;
  const endX = startX + (Math.random() - 0.5) * 40;
  
  return (
    <motion.div
      initial={{ 
        x: `${startX}vw`, 
        y: "110vh", 
        opacity: 0,
        scale: 0.5 
      }}
      animate={{ 
        x: `${endX}vw`, 
        y: "-10vh", 
        opacity: [0, 0.8, 0.8, 0],
        scale: [0.5, 1, 1, 0.5]
      }}
      transition={{ 
        duration: duration,
        delay: delay,
        repeat: Infinity,
        ease: "linear"
      }}
      className="fixed pointer-events-none z-0"
      style={{ fontSize: size }}
    >
      {emoji}
    </motion.div>
  );
};

// Mood-based ambient background
export const MoodAtmosphere = ({ intensity = "medium" }) => {
  const { moodTheme, dynamicThemeEnabled, currentMood } = useMoodTheme();
  const [particles, setParticles] = useState([]);

  // Generate particles based on mood
  useEffect(() => {
    if (!dynamicThemeEnabled || !currentMood) {
      setParticles([]);
      return;
    }

    const particleCount = intensity === "high" ? 12 : intensity === "medium" ? 8 : 4;
    const newParticles = [];

    for (let i = 0; i < particleCount; i++) {
      newParticles.push({
        id: i,
        emoji: moodTheme.particles,
        delay: Math.random() * 10,
        duration: 15 + Math.random() * 10,
        size: `${1 + Math.random() * 1.5}rem`
      });
    }

    setParticles(newParticles);
  }, [moodTheme, dynamicThemeEnabled, currentMood, intensity]);

  if (!dynamicThemeEnabled) return null;

  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
      {/* Gradient overlay */}
      <motion.div
        key={currentMood}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.5 }}
        className="absolute inset-0"
        style={{ 
          background: moodTheme.bgGradient,
        }}
      />
      
      {/* Floating particles */}
      <AnimatePresence>
        {particles.map(particle => (
          <FloatingParticle
            key={particle.id}
            emoji={particle.emoji}
            delay={particle.delay}
            duration={particle.duration}
            size={particle.size}
          />
        ))}
      </AnimatePresence>
    </div>
  );
};

// Mood indicator badge with animation
export const MoodBadge = ({ mood, size = "md", showLabel = true, animated = true }) => {
  const theme = MOOD_THEMES[mood];
  
  if (!theme) return null;

  const sizeClasses = {
    sm: "w-8 h-8 text-lg",
    md: "w-12 h-12 text-2xl",
    lg: "w-16 h-16 text-3xl",
    xl: "w-20 h-20 text-4xl"
  };

  return (
    <div className="flex flex-col items-center gap-2">
      <motion.div
        animate={animated ? { 
          scale: [1, 1.1, 1],
          rotate: [0, 5, -5, 0]
        } : {}}
        transition={{ 
          duration: 2,
          repeat: Infinity,
          ease: "easeInOut"
        }}
        className={`${sizeClasses[size]} rounded-full flex items-center justify-center`}
        style={{ 
          background: `linear-gradient(135deg, ${theme.primary}30, ${theme.secondary}30)`,
          border: `2px solid ${theme.primary}50`,
          boxShadow: `0 0 20px ${theme.primary}30`
        }}
      >
        {theme.emoji}
      </motion.div>
      {showLabel && (
        <span 
          className="text-xs font-rajdhani font-semibold"
          style={{ color: theme.primary }}
        >
          {theme.name}
        </span>
      )}
    </div>
  );
};

// Mood-themed card with dynamic styling
export const MoodThemedCard = ({ mood, children, className = "" }) => {
  const theme = MOOD_THEMES[mood] || MOOD_THEMES.neutral;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`relative overflow-hidden rounded-2xl ${className}`}
      style={{
        background: `linear-gradient(135deg, ${theme.primary}10, ${theme.secondary}10)`,
        border: `1px solid ${theme.primary}30`,
      }}
    >
      {/* Accent line */}
      <div 
        className="absolute top-0 left-0 right-0 h-1"
        style={{ 
          background: `linear-gradient(90deg, ${theme.primary}, ${theme.secondary})`
        }}
      />
      
      {/* Content */}
      <div className="p-4">
        {children}
      </div>
    </motion.div>
  );
};

// Visual mood timeline/garden component
export const MoodGarden = ({ moods = [], maxItems = 7 }) => {
  const recentMoods = moods.slice(0, maxItems);
  
  if (recentMoods.length === 0) {
    return (
      <div className="text-center py-8 text-slate-400">
        <p className="text-sm">Tu jardín emocional está vacío</p>
        <p className="text-xs mt-1">Registra tu primer estado de ánimo</p>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Timeline line */}
      <div className="absolute left-6 top-0 bottom-0 w-px bg-gradient-to-b from-neon-cyan/50 via-neon-purple/50 to-transparent" />
      
      {/* Mood items */}
      <div className="space-y-4">
        {recentMoods.map((mood, index) => {
          const theme = MOOD_THEMES[mood.mood] || MOOD_THEMES.neutral;
          const isLatest = index === 0;
          
          return (
            <motion.div
              key={mood.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              className="flex items-start gap-4 pl-2"
            >
              {/* Node */}
              <motion.div
                animate={isLatest ? { 
                  scale: [1, 1.2, 1],
                  boxShadow: [
                    `0 0 0 0 ${theme.primary}40`,
                    `0 0 0 8px ${theme.primary}00`,
                  ]
                } : {}}
                transition={{ duration: 2, repeat: Infinity }}
                className="relative z-10 w-8 h-8 rounded-full flex items-center justify-center text-lg flex-shrink-0"
                style={{
                  background: `linear-gradient(135deg, ${theme.primary}30, ${theme.secondary}30)`,
                  border: `2px solid ${theme.primary}`,
                }}
              >
                {theme.emoji}
              </motion.div>
              
              {/* Content */}
              <div className="flex-1 min-w-0 pb-2">
                <div className="flex items-center gap-2">
                  <span 
                    className="font-rajdhani font-semibold"
                    style={{ color: theme.primary }}
                  >
                    {theme.name}
                  </span>
                  <span className="text-xs text-slate-500">
                    {new Date(mood.timestamp).toLocaleDateString('es-ES', {
                      day: 'numeric',
                      month: 'short',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </div>
                {mood.note && (
                  <p className="text-sm text-slate-400 truncate mt-1">
                    {mood.note}
                  </p>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

// Mood ring/wheel visualization
export const MoodWheel = ({ currentMood, size = 200 }) => {
  const allMoods = Object.keys(MOOD_THEMES);
  const angleStep = 360 / allMoods.length;
  const radius = size / 2 - 30;

  return (
    <div 
      className="relative"
      style={{ width: size, height: size }}
    >
      {/* Center current mood */}
      <motion.div
        className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10"
        animate={{ scale: [1, 1.1, 1] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <MoodBadge mood={currentMood || "neutral"} size="lg" showLabel={false} animated={false} />
      </motion.div>

      {/* Mood ring */}
      {allMoods.map((mood, index) => {
        const angle = (index * angleStep - 90) * (Math.PI / 180);
        const x = Math.cos(angle) * radius + size / 2;
        const y = Math.sin(angle) * radius + size / 2;
        const theme = MOOD_THEMES[mood];
        const isActive = mood === currentMood;

        return (
          <motion.div
            key={mood}
            className="absolute"
            style={{
              left: x - 16,
              top: y - 16,
            }}
            animate={isActive ? { 
              scale: [1, 1.3, 1],
            } : { scale: 1 }}
            transition={{ duration: 1.5, repeat: isActive ? Infinity : 0 }}
          >
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-lg transition-all duration-300 ${
                isActive ? "ring-2" : "opacity-50"
              }`}
              style={{
                background: `${theme.primary}20`,
                borderColor: theme.primary,
                boxShadow: isActive ? `0 0 15px ${theme.primary}50` : 'none'
              }}
              title={theme.name}
            >
              {theme.emoji}
            </div>
          </motion.div>
        );
      })}

      {/* Connecting lines */}
      <svg 
        className="absolute inset-0 pointer-events-none opacity-20"
        width={size} 
        height={size}
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="url(#mood-gradient)"
          strokeWidth="1"
          strokeDasharray="4 4"
        />
        <defs>
          <linearGradient id="mood-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#00F3FF" />
            <stop offset="100%" stopColor="#A855F7" />
          </linearGradient>
        </defs>
      </svg>
    </div>
  );
};

// Settings widget for mood theme
export const MoodThemeSettings = () => {
  const { dynamicThemeEnabled, toggleDynamicTheme, currentMood, moodTheme } = useMoodTheme();

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="font-rajdhani font-semibold text-white">Tema Dinámico</h4>
          <p className="text-xs text-slate-400">
            La interfaz cambia según tu estado de ánimo
          </p>
        </div>
        <button
          onClick={toggleDynamicTheme}
          className={`relative w-12 h-6 rounded-full transition-colors ${
            dynamicThemeEnabled ? "bg-neon-cyan" : "bg-slate-600"
          }`}
        >
          <motion.div
            className="absolute top-1 w-4 h-4 bg-white rounded-full"
            animate={{ left: dynamicThemeEnabled ? "calc(100% - 20px)" : "4px" }}
            transition={{ type: "spring", stiffness: 500, damping: 30 }}
          />
        </button>
      </div>

      {dynamicThemeEnabled && currentMood && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          className="p-3 rounded-lg"
          style={{
            background: `linear-gradient(135deg, ${moodTheme.primary}10, ${moodTheme.secondary}10)`,
            border: `1px solid ${moodTheme.primary}30`
          }}
        >
          <div className="flex items-center gap-3">
            <span className="text-2xl">{moodTheme.emoji}</span>
            <div>
              <p className="text-sm font-rajdhani font-semibold" style={{ color: moodTheme.primary }}>
                Tema: {moodTheme.name}
              </p>
              <p className="text-xs text-slate-400">
                {moodTheme.description}
              </p>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default MoodAtmosphere;
